import React, { useEffect, useState, useCallback } from 'react';
//CSS
import styles from './ImportTemplate.module.css';
import layout from '../../../Modules/layout.module.css'
import FloatingLabelSelect from '../../Utils/Form/FloatingLabelSelect/FloatingLabelSelect';
import { FloatingLabelSelectOption } from '../../../Types/Form/FloatingLabelSelectOption';
import Form from '../../Utils/Form/Form';
import Button from '../../Utils/Button/Button';
import LoadingPopup from '../../Utils/LoadingPopup/LoadingPopup';
import FloatingLabelMultiSelect from "../../Utils/Form/FloatingLabelMultiSelect/FloatingLabelMultiSelect";
import {ISelectOption} from "../../Utils/MultiSelect/MultiSelect";
import {axiosInstance} from "../../../Authentication/AxiosInterceptor";
import {useAppSelector} from "../../../Redux/Store/Store";
import {getClient, getClientRolePermission} from "../../../Redux/Store/Slices/client";
import { toast } from 'react-toastify';
import { ProductType } from '../../../Types/ProductType';
//Components
//Data

type ImportTemplateInformationResponse = {
    partners: ISelectOption[];
    departments: ISelectOption[];
    groups: ISelectOption[];
    templates: ISelectOption[];
    observees: ISelectOption[];
}

type ImportTemplateProps = {
    importHandler: (templateId: string, observee?: string, departmentId?: string, partnerId?: string, groupIds?: string[]) => void | Promise<void>,
    productType: ProductType,
};

const ImportTemplate = ({ importHandler, productType } : ImportTemplateProps) => {

    const client = useAppSelector(getClient);
    const rolePermissions = useAppSelector(getClientRolePermission);

    const [partners, setPartners] = useState<FloatingLabelSelectOption[]>([]);
    const [selectedPartner, setSelectedPartner] = useState<FloatingLabelSelectOption>();

    // Departments
    const [departments, setDepartments] = useState<FloatingLabelSelectOption[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<FloatingLabelSelectOption>();

    // Groups   
    const [groups, setGroups] = useState<ISelectOption[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<ISelectOption[]>([]);

    // Templates
    const [templates, setTemplates] = useState<FloatingLabelSelectOption[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<FloatingLabelSelectOption>();

    const [observees, setObservees] = useState<FloatingLabelSelectOption[]>([]);
    const [selectedObservee, setSelectedObservee] = useState<FloatingLabelSelectOption>();

    const [isImporting, setIsImporting] = useState(false);

    const getData = useCallback(async () => {
        if(client){
            try {
                // Use GetImportTemplateInformation which already filters out disabled templates on the backend
                const response = await axiosInstance.get<ImportTemplateInformationResponse>(`GetImportTemplateInformation/${client.id}/${productType}`);
                const importData = response.data;

                setPartners   (importData.partners);
                setDepartments(importData.departments);
                setTemplates  (importData.templates);
                setGroups     (importData.groups);
                setObservees   (importData.observees);
            } catch (error) {
                console.error('Error fetching import template data:', error);
            }
        }
    }, [client]);

    useEffect(() => {
        getData()
    }, [getData]);

    const handleTemplateSelected = (value: string) => {
        const found = templates.find(x => x.value === value);
        setSelectedTemplate(found);
    };

    const handlePartnerSelected = (value: string) => {
        const found = partners.find(x => x.value === value);

        console.log("Partner selected:", found);

        if(selectedDepartment){
            setSelectedDepartment(undefined);
            toast.warn("Department selection removed. Only one partner or department can be selected");
        }

        if(found){
            setSelectedPartner(found);
        }
    };

    const handleDepartmentSelected = (value: string) => {
        const found = departments.find(x => x.value === value);

        console.log("Department selected:", found);

        if(selectedPartner){
            setSelectedPartner(undefined);
            toast.warn("Partner selection removed. Only one partner or department can be selected");
        }

        if(found){
            setSelectedDepartment(found);
        }
    };

    const handleGroupsSelected = (values: ISelectOption[]) => {
        // keep only the group objects whose .value is in the incoming values array
        const found = groups.filter(g => values.map(v => v.value).includes(g.value));
        setSelectedGroups(found);
    };

    const handleObserveeSelected = (value: string) => {
        const found = observees.find(x => x.value === value);
        setSelectedObservee(found);
    };

    const handleSubmit = async () => {
        if (isImporting) {
            return;
        }

        if (!selectedTemplate?.value) {
            toast.error('Please select a template to import');
            return;
        }

        // Skip third party organization validation for Develop
        if (productType !== ProductType.Develop) {
            const hasPartnerOptions = partners.length > 0;
            const hasDepartmentOptions = departments.length > 0;
            const requiresAssignment = (hasPartnerOptions || hasDepartmentOptions) && !rolePermissions?.accessAllClientSubsets;

            const hasPartnerSelected = !!selectedPartner?.value;
            const hasDepartmentSelected = !!selectedDepartment?.value;

            if (requiresAssignment && !hasPartnerSelected && !hasDepartmentSelected) {
                toast.error("Please select either a Partner or a Department");
                return;
            }

            if (hasPartnerSelected && hasDepartmentSelected) {
                toast.error("Please select only one: Partner or Department");
                return;
            }
        }
        else{
            if(!selectedObservee?.value){
                toast.error("Please select a user to complete for");
                return;
            }
        }

        const departmentId = productType === ProductType.Develop ? undefined : (selectedDepartment?.value ? selectedDepartment.value : undefined);
        const partnerId = productType === ProductType.Develop ? undefined : (selectedPartner?.value ? selectedPartner.value : undefined);
        const groupIds = selectedGroups
            .filter(g => g.value && g.value !== '')
            .map(x => x.value);

        setIsImporting(true);
        try {
            await Promise.resolve(
                importHandler(selectedTemplate.value, selectedObservee?.value, departmentId, partnerId, groupIds)
            );
        } finally {
            setIsImporting(false);
        }
    };

    // Hide third party organization fields for Develop
    const showThirdPartyFields = productType !== ProductType.Develop;

    return (
        <div className={`${layout.column}`} style={{height: "100%", gap: "40px"}}>
            <LoadingPopup isVisible={isImporting} message="Importing..." />
            <Form>
                    <FloatingLabelSelect onChange={(e) => handleTemplateSelected(e.target.value)} label={"Select a Template to import"} options={templates} value={selectedTemplate?.value} required={true} noOptionsMessage='You have no mandated templates'></FloatingLabelSelect>

                    {showThirdPartyFields && (
                        <>
                            <FloatingLabelSelect onChange={(e) => handleDepartmentSelected(e.target.value)} label={"Select a Department to assign"} options={departments} value={selectedDepartment?.value} noOptionsMessage='You are not a member of any Department'></FloatingLabelSelect>

                            <FloatingLabelSelect onChange={(e) => handlePartnerSelected(e.target.value)} label={"Select a Partner to assign"} options={partners} value={selectedPartner?.value} noOptionsMessage='You are not a member of any Partner'></FloatingLabelSelect>

                            {(partners.length > 0 || departments.length > 0) && !rolePermissions?.accessAllClientSubsets && (
                                <p className={styles.required}>
                                    Select either a Partner or a Department (required).
                                </p>
                            )}
                        </>
                    )}

                    {productType === ProductType.Develop && <FloatingLabelSelect onChange={(e) => handleObserveeSelected(e.target.value)} label={"Select a user to complete for"} options={observees} value={selectedObservee?.value} required={true} noOptionsMessage='This client has no users to complete for'></FloatingLabelSelect>}

                    <FloatingLabelMultiSelect onChange={(e) => handleGroupsSelected(e)} label={"Select a Group to assign"} availableOptions={groups} selectedOptions={selectedGroups}></FloatingLabelMultiSelect>

                    <div className={`${styles.createButton}`}>
                        {selectedTemplate?.value && <p className={styles.required}>Fields indicated with * are required</p>}
                        <Button clickHandler={handleSubmit} colour="#52BFA2" textColour="#FFF" disabled={!selectedTemplate?.value || isImporting}>{isImporting ? 'Importing...' : 'Create Diagnostic'}</Button>
                    </div>
            </Form>
        </div>
      );
};

export default ImportTemplate;